import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useCartUI } from '../../contexts/CartUIContext';
import { useAuth } from '../../contexts/AuthContext';

const Header = () => {
  const { totalItems } = useCart();
  const { toggleCart } = useCartUI();
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { to: '/', label: 'Inicio', icon: '🏠' },
    { to: '/productos', label: 'Productos', icon: '🍕' },
    ...(isAuthenticated ? [{ to: '/historial', label: 'Mis pedidos', icon: '📋' }] : []),
    ...(isAuthenticated && user?.role === 'admin' ? [{ to: '/admin', label: 'Admin', icon: '👑' }] : []),
    ...(isAuthenticated && user?.role === 'motorizado' ? [{ to: '/driver', label: 'Motorizado', icon: '🚗' }] : []),
  ];

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 px-4 md:px-8 py-3 md:py-4 transition flex justify-between items-center ${
        scrolled ? 'bg-background/95 backdrop-blur-md shadow-sm border-b border-primary/5' : 'bg-background/90 backdrop-blur-sm'
      }`}>
        <Link to="/" className="font-serif text-xl md:text-2xl font-bold text-primary">
          Tu<span className="text-gold italic">pastel</span>
        </Link>
        
        {/* Nav desktop */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map(link => (
            <Link
              key={link.to}
              to={link.to}
              className={`px-4 py-2 text-sm font-medium rounded-full transition ${
                isActive(link.to) ? 'text-gold bg-gold/10' : 'hover:bg-gold/5'
              }`}
            >
              {link.label}
            </Link>
          ))}
          {isAuthenticated ? (
            <button 
              onClick={handleLogout} 
              className="px-4 py-2 text-sm font-medium text-red-500 hover:bg-red-50 rounded-full transition"
            >
              Salir
            </button>
          ) : (
            <Link 
              to="/login" 
              className="px-4 py-2 text-sm font-medium text-gold hover:bg-gold/5 rounded-full transition"
            >
              Iniciar sesión
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-2">
          <button 
            onClick={toggleCart}
            className="relative p-2 rounded-full hover:bg-gold/5 transition"
            aria-label="Abrir carrito"
          >
            <span className="text-xl">🛒</span>
            {totalItems > 0 && (
              <span className="absolute -top-1 -right-1 bg-gold text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {totalItems}
              </span>
            )}
          </button>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-full hover:bg-gold/5 transition"
            aria-label="Menú"
          >
            <div className="w-6 h-5 flex flex-col justify-between">
              <span className={`w-full h-0.5 bg-primary transition-all ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`} />
              <span className={`w-full h-0.5 bg-primary transition-all ${mobileMenuOpen ? 'opacity-0' : ''}`} />
              <span className={`w-full h-0.5 bg-primary transition-all ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
            </div>
          </button>
        </div>
      </header>

      {/* Menú móvil */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm" 
            onClick={() => setMobileMenuOpen(false)} 
          />
          <div className="absolute top-16 left-0 right-0 bg-white shadow-2xl rounded-b-3xl animate-slide-down">
            <div className="p-6 space-y-2">
              {isAuthenticated && (
                <div className="pb-4 mb-4 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gold to-gold-dark flex items-center justify-center text-white font-bold text-lg">
                      {user?.name?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div>
                      <div className="font-semibold">{user?.name}</div>
                      <div className="text-xs text-gray-500 capitalize">{user?.role}</div>
                    </div>
                  </div>
                </div>
              )}

              {navLinks.map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition ${
                    isActive(link.to) 
                      ? 'bg-gold/10 text-gold font-semibold' 
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <span className="text-xl">{link.icon}</span>
                  <span>{link.label}</span>
                </Link>
              ))}

              <div className="pt-4 border-t border-gray-100">
                {isAuthenticated ? (
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-500 hover:bg-red-50 transition"
                  >
                    <span className="text-xl">🚪</span>
                    <span>Cerrar sesión</span>
                  </button>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gold text-white font-semibold"
                    >
                      <span className="text-xl">🔑</span>
                      <span>Iniciar sesión</span>
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-50 mt-2"
                    >
                      <span className="text-xl">✨</span>
                      <span>Crear cuenta</span>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
